import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import FormHOC from './FormHOC'
import Button from '../elements/Button'
import FancySelectAdvancedRow from '../elements/FancySelectAdvancedRow'
import Checkbox from '../elements/Checkbox'
import HomeSecurePlan from '../components/HomeSecurePlan'
import { TimelineLite } from 'gsap/TimelineLite'

const options = {
    postUrl: '/api/postSubscribeHomeSecure',
    success: null,
    dontScrollToNav: false
}

const state = {
    termsAndConditions: false,
    referrerCode: '',
    serviceId: null
}

const DEFAULT_HEADING = 'Subscribe to Home Secure - get first 3 months FREE<br />Network level protection for just $6/mth'
const DEFAULT_SUB_HEADING = 'Take control of your home internet security and content settings. No lock-in.'

const HomeSecureSubscribeForm = (props) => {
    const [heading, setHeading] = useState(DEFAULT_HEADING)
    const [subHeading, setSubHeading] = useState(DEFAULT_SUB_HEADING)
    const [isSubscribed, setIsSubscribed] = useState(false)

    const formRef = useRef(null)

    useEffect(() => {
        selectAddressIfSingle()
        if (props.serviceId && props.homeSecureServices.some(el => el.id === props.serviceId)) {
            handleChangeAddress(props.serviceId)
        }
    }, [props.serviceId, props.homeSecureServices])

    const handleChangeAddress = serviceId => {
        const tl = new TimelineLite()
        if (formRef.current) {
            tl.to(formRef.current, 0.2, { opacity: 0 })
            tl.add(() => {
                props.setDynamicValues({
                    serviceId: serviceId
                })

                setIsSubscribed(false)
                const hsService = props.homeSecureServices.find(el => { return el.id === serviceId })
                if (hsService) {
                    /*
                        We have 4 types of users here
                            1. Users who have not subscribed and are in free trial
                            2. Users who have had an HS service/trial and have since cancelled
                            3. Users who have subscribed
                            4. Users who have never had a subscription or trial
                    */
                    if (isNotSubscribedAndInFreeTrial(hsService)) {
                        setHeading('Early-bird Home Secure offer - get an extra month free')
                        setSubHeading('Subscribe to Home Secure before your free trial ends, and we’ll extend your free trial for an extra month. Home Secure is just <span class="bold">$6/mth</span> thereafter. No lock-in.')
                    } else if (isCancelledOrTrialExpired(hsService)) {
                        setHeading('Get network level protection covering all your connected devices with Home Secure')
                        setSubHeading('Subscribe to Home Secure for just <span class="bold">$6/mth</span> and enjoy real peace-of-mind online. No lock-in.')
                    } else if (hsService.isSubscribed) {
                        setIsSubscribed(true)
                    } else {
                        setHeading(DEFAULT_HEADING)
                        setSubHeading(DEFAULT_SUB_HEADING)
                    }
                }
            })
            tl.to(formRef.current, 0.2, { opacity: 1, delay: 0.2 })
        }
    }

    const selectAddressIfSingle = () => {
        if (props.homeSecureServices.length === 1) {
            const onlyAddressServiceId = props.homeSecureServices[0].id
            props.setDynamicValues({
                serviceId: onlyAddressServiceId
            }, () => {
                handleChangeAddress(onlyAddressServiceId)
            })
        }
    }

    const isNotSubscribedAndInFreeTrial = data => {
        return !data.isSubscribed && data.isTrial && !data.isCancelled
    }

    const isCancelledOrTrialExpired = data => {
        return !data.isSubscribed && !data.isNonExistingService
    }

    const addresses = () => {
        const addresses = []
        props.homeSecureServices.forEach(item => {
            addresses.push({
                value: item.id,
                label: item.address
            })
        })

        return addresses
    }

    if (props.homeSecureServices.length === 0) {
        return <p className='text-center row-error'>Your account has no active Exetel broadband services which are Home Secure ready.</p>
    }

    const Subscribed = (
        <div className='row'>
            <div className='col-12 col-lg-8 offset-lg-2 text-center'>
                <p className='m-0 mb-2 bold font-medium'>You’re already subscribed.</p>
                <p className='m-0 lh'>
                    Your internet service and all connected devices are protected by Home Secure.
                </p>
            </div>
        </div>
    )

    return (
        <div>
            <div className='row mb-4'>
                <div className='col-12 col-md-8 offset-md-2'>
                    <FancySelectAdvancedRow
                        label='Select your broadband service address'
                        name='address'
                        options={addresses()}
                        value={props.values.serviceId}
                        handleChange={data => handleChangeAddress(data.address)}
                        errors={props.errors}
                    />
                </div>
            </div>
            <div ref={formRef} className='row'>
                <div className='col-12'>
                    {isSubscribed
                        ? Subscribed
                        : <React.Fragment>
                            <div className='row mb-5'>
                                <div className='col-12 col-md-10 offset-md-1'>
                                    <HomeSecurePlan heading={heading} subHeading={subHeading} />
                                </div>
                            </div>
                            <div className='row mb-lg-4'>
                                <div className='col-12 col-md-8 offset-md-2'>
                                    <Checkbox
                                        name='termsAndConditions'
                                        errors={props.errors}
                                        value={props.values.termsAndConditions}
                                        handleClick={value => props.setDynamicValues({ termsAndConditions: value })}
                                    >
                                        I have read the <a href='#todo' target='_blank'>Critical Information Summary</a>, <a href='#todo' target='_blank'>Terms &amp; Conditions</a> and <a href='https://files.exetel.com.au/terms/ExetelPrivacyPolicy.pdf' target='_blank'>Privacy Policy</a> and have agreed to make this purchase.
                                    </Checkbox>
                                    <br />
                                    {props.children}
                                </div>
                            </div>
                            <div className='row'>
                                <div className='col-12 col-md-4 offset-md-2 text-center text-md-left mt-2 mb-4 mb-lg-2'>
                                    <Button title='Subscribe now' action={props.handleSubmit} />
                                </div>
                                <div className='col-12 col-md-4 text-center text-md-right my-2'>
                                    <input placeholder='Referrer Code' name='referrerCode' value={props.values.referrerCode} onChange={props.handleInputChange} />
                                </div>
                            </div>
                          </React.Fragment>}
                </div>
            </div>
        </div>
    )
}

HomeSecureSubscribeForm.propTypes = {
    homeSecureServices: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        address: PropTypes.string,
        isTrial: PropTypes.bool,
        isSubscribed: PropTypes.bool,
        isCancelled: PropTypes.bool
    })).isRequired,
    serviceId: PropTypes.number
}

export default FormHOC(HomeSecureSubscribeForm, state, options)



// WEBPACK FOOTER //
// ./resources/js/forms/HomeSecureSubscribeForm.js